// src/lib/dashboard/kpisDepartamento.ts
//
// KPIs dos departamentos (tabela `kpis_departamento`): cada gestor propõe até 3
// indicadores e o Igor valida. As regras de quem valida e do limite de 3 moram
// no banco (trigger `kpis_departamento_regras`); aqui ficam a lista de
// departamentos da tela e a leitura do número.
//
// Função pura: roda sob `node --test` com type-stripping.

/** Mesmo texto da permissão no banco (`pode_validar_kpi`) e em Config > Permissões. */
export const VALIDAR_KPI_PERMISSION = 'Validar KPIs dos departamentos';

export const LIMITE_KPIS_POR_DEPARTAMENTO = 3;

/** Lista fechada na tela, texto livre no banco: departamento novo entra só aqui. */
export const DEPARTAMENTOS: { id: string; rotulo: string }[] = [
  { id: 'comercial', rotulo: 'Comercial' },
  { id: 'financeiro', rotulo: 'Financeiro' },
  { id: 'gestao', rotulo: 'Gestão de clientes' },
  { id: 'geradoras', rotulo: 'Geradoras' },
  { id: 'marketing', rotulo: 'Marketing' },
  { id: 'tecnologia', rotulo: 'Tecnologia' },
];

export const rotuloDepartamento = (id: string): string => DEPARTAMENTOS.find((d) => d.id === id)?.rotulo ?? id;

export type UnidadeKpi = 'numero' | 'percentual' | 'reais' | 'kwh' | 'dias' | 'horas';
export type StatusKpi = 'proposto' | 'validado' | 'recusado';

export const UNIDADES_KPI: { id: UnidadeKpi; rotulo: string }[] = [
  { id: 'numero', rotulo: 'Número' },
  { id: 'percentual', rotulo: '%' },
  { id: 'reais', rotulo: 'R$' },
  { id: 'kwh', rotulo: 'kWh' },
  { id: 'dias', rotulo: 'Dias' },
  { id: 'horas', rotulo: 'Horas' },
];

export interface KpiDepartamento {
  id: number;
  departamento: string;
  nome: string;
  como_medir: string | null;
  unidade: UnidadeKpi;
  sentido: 'maior' | 'menor';
  meta: number;
  valor_atual: number | null;
  valor_atualizado_em: string | null;
  status: StatusKpi;
  comentario_validacao: string | null;
  proposto_por: string | null;
  proposto_em: string;
  validado_por: string | null;
  validado_em: string | null;
  /** Id do cálculo automático (kpisAutomaticos.ts). Preenchido: o valor sai ao vivo. */
  calculo_automatico: string | null;
}

/** Valor já com a unidade: "R$ 120 mil", "92%", "1.240 kWh". */
export function formatarValorKpi(valor: number | null | undefined, unidade: UnidadeKpi): string {
  if (valor == null || !Number.isFinite(Number(valor))) return '—';
  const v = Number(valor);
  const num = (n: number, casas = 1) => n.toLocaleString('pt-BR', { maximumFractionDigits: casas });
  switch (unidade) {
    case 'reais':
      return Math.abs(v) >= 10_000 ? `R$ ${num(v / 1000, 0)} mil` : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    case 'percentual': return `${num(v)}%`;
    case 'kwh': return `${num(v, 0)} kWh`;
    case 'dias': { const r = Math.round(v * 10) / 10; return `${num(r)} ${r === 1 ? 'dia' : 'dias'}`; }
    case 'horas': return `${num(v)} h`;
    default: return num(v);
  }
}

/**
 * Quanto do caminho até a meta o KPI já andou, sempre "1 = bateu".
 *
 * Em KPI de "quanto menor, melhor" (inadimplência, prazo) a conta inverte:
 * meta 5% com 4% atingido = bateu (1,25); com 10% = metade (0,5).
 */
export function atingimentoKpi(k: Pick<KpiDepartamento, 'meta' | 'valor_atual' | 'sentido'>): number | null {
  if (k.valor_atual == null) return null;
  const meta = Number(k.meta);
  const atual = Number(k.valor_atual);
  if (!Number.isFinite(meta) || !Number.isFinite(atual)) return null;
  if (k.sentido === 'menor') {
    if (atual <= 0) return meta >= 0 ? 1 : null;
    return meta / atual;
  }
  if (meta === 0) return atual >= 0 ? 1 : 0;
  return atual / meta;
}

/** Ativos contam no limite de 3: tudo que não foi recusado. */
export const kpiAtivo = (k: Pick<KpiDepartamento, 'status'>): boolean => k.status !== 'recusado';
